import { useContext, useEffect, useState } from 'react';
import { CostTypeRef } from '@leedpulse-monorepo/enums';
import CompanyContext from '../../contexts/company/CompanyContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { getLeedbucksConfig, LeedbucksConfig, LeedbucksConfigScope } from '../../../domain/use_cases/leedflow/getLeedbucksConfig';
import { leedbucksRateOf } from '../../helpers';

const EMPTY_CONFIG: LeedbucksConfig = { unit_value: 0, actions: {} };

// Rates at 0 read as an empty bundle, not as a missing value: retry before giving up.
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** 
 * Backend leedbucks rates, so the minutes/SMS equivalents are never hardcoded on the frontend.
 * The global ones by default; `scope` asks for a subscription's, or every subscription's for the overview.
 */
export const useLeedbucksConfig = ({ subscriptionId, allSubscriptions }: LeedbucksConfigScope = {}) => {
  const { activeCompany } = useContext(CompanyContext);
  const { authentication } = useContext(AuthenticationContext);

  const [config, setConfig] = useState<LeedbucksConfig>(EMPTY_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!activeCompany?.api_key || !authentication?.token) {
      // Reset before the fetch, not a value derivable during render.
      // eslint-disable-next-line react/set-state-in-effect
      setConfig(EMPTY_CONFIG);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    const fetchConfig = async () => {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          const fetched = await getLeedbucksConfig(activeCompany.api_key, authentication, { subscriptionId, allSubscriptions });
          if (cancelled) return;
          setConfig(fetched);
          setIsLoading(false);
          return;
        } catch (err) {
          if (attempt < MAX_ATTEMPTS) {
            await wait(RETRY_DELAY_MS * attempt);
            continue;
          }
          console.error('useLeedbucksConfig – error fetching config:', err);
          if (cancelled) return;
          setConfig(EMPTY_CONFIG);
          setIsError(true);
          setIsLoading(false);
        }
      }
    };
    fetchConfig();

    return () => {
      cancelled = true;
    };
  }, [activeCompany?.api_key, authentication, subscriptionId, allSubscriptions]);

  const rateFor = (typeRef: CostTypeRef): number | undefined => leedbucksRateOf(config, typeRef);

  return {
    config,
    isLoading,
    isError,
    unitValue: config.unit_value,
    callRate: rateFor(CostTypeRef.CALL),
    smsRate: rateFor(CostTypeRef.SMS_OUTBOUND),
    receptionistRate: rateFor(CostTypeRef.CALL_RECEPTIONIST),
    rateFor,
  };
};
